import React, { useRef, useEffect } from 'react';
import { FiSend, FiUser, FiCpu, FiDatabase, FiMessageCircle } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';

// Hilfsfunktion, um **Text** und *Text* in <strong>/<em> umzuwandeln und Umlaute zu reparieren
function renderWithFormatting(text) {
  // Repariere Umlaute zuerst
  let cleanText = text
    .replace(/Ã¤/g, 'ä')
    .replace(/Ã¶/g, 'ö')
    .replace(/Ã¼/g, 'ü')
    .replace(/ÃŸ/g, 'ß')
    .replace(/Ã„/g, 'Ä')
    .replace(/Ã–/g, 'Ö')
    .replace(/Ãœ/g, 'Ü');

  // Ersetzt **Text** durch <strong>Text</strong> (fett)
  cleanText = cleanText.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  // Ersetzt *Text* durch <em>Text</em> (kursiv) - nur wenn nicht bereits in ** enthalten
  cleanText = cleanText.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');
  return { __html: cleanText };
}

function ChatWindow({ messages, isLoading, error, input, setInput, handleSendMessage, chatMode }) {
  const { isDarkMode } = useAuth();
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  // Auto-Resize der Textarea (max 6 Zeilen)
  const adjustTextareaHeight = () => {
    const { current: textarea } = textareaRef;
    if (textarea) {
      textarea.style.height = 'auto';
      const { scrollHeight } = textarea;
      const maxHeight = 6 * 24; // 6 Zeilen * 24px (line-height)
      textarea.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [input]);

  // Shift+Enter für neue Zeile, Enter zum Senden
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isLoading && input.trim()) {
        handleSendMessage();
      }
    }
  };

  return (
    <div
      className={`lg:col-span-6 flex flex-col rounded-lg border h-full ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white/50 border-gray-200'}`}
    >
      {/* Chat Header mit Modus-Anzeige */}
      <div className={`p-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <h3 className="font-medium">KI-Chat</h3>
            <div
              className={`ml-3 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                chatMode === 'documents'
                  ? `${isDarkMode ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-800'}`
                  : `${isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-100 text-green-800'}`
              }`}
            >
              {chatMode === 'documents' ? (
                <>
                  <FiDatabase className="mr-1" />
                  Dokumentenmodus
                </>
              ) : (
                <>
                  <FiMessageCircle className="mr-1" />
                  Freier Modus
                </>
              )}
            </div>
          </div>
          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {messages.length} Nachrichten
          </div>
        </div>
      </div>

      <div className="flex-grow p-4 overflow-y-auto">
        {messages.map((msg, index) => (
          <div
            key={index}
            className={`mb-4 flex items-start gap-3 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            {msg.role !== 'user' && (
              <div className={`p-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                <FiCpu />
              </div>
            )}
            <div
              className={`p-4 rounded-lg max-w-4xl ${msg.role === 'user' ? 'bg-blue-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-100' : 'bg-gray-100 text-gray-900'}`}
            >
              {msg.role === 'user' ? (
                <p className="whitespace-pre-wrap">{msg.content}</p>
              ) : (
                <p
                  className="whitespace-pre-wrap"
                  dangerouslySetInnerHTML={renderWithFormatting(msg.content)}
                />
              )}
            </div>
            {msg.role === 'user' && (
              <div className="p-2 bg-blue-600 rounded-full">
                <FiUser />
              </div>
            )}
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start gap-3">
            <div className={`p-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <FiCpu />
            </div>
            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
              <div
                className={`flex items-center gap-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
              >
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                <span>Denke nach...</span>
              </div>
            </div>
          </div>
        )}
        {error && !isLoading && (
          <div className="flex justify-start gap-3">
            <div className="p-2 bg-red-800/50 rounded-full">
              <FiCpu />
            </div>
            <div className="p-3 rounded-lg bg-red-800/50 text-red-300">
              <p>{error}</p>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className={`flex items-end rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
          <textarea
            ref={textareaRef}
            rows={1}
            className={`flex-grow bg-transparent p-3 focus:outline-none resize-none overflow-y-auto ${isDarkMode ? 'text-white placeholder-gray-400' : 'text-gray-900 placeholder-gray-500'}`}
            style={{ minHeight: '48px', maxHeight: '144px', lineHeight: '24px' }}
            placeholder={
              chatMode === 'documents'
                ? 'Fragen Sie mich etwas... (Shift+Enter für neue Zeile)'
                : 'Stellen Sie mir eine beliebige Frage... (Shift+Enter für neue Zeile)'
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading}
          />
          <button
            type="button"
            onClick={handleSendMessage}
            disabled={isLoading || !input.trim()}
            className={`p-3 disabled:cursor-not-allowed ${isDarkMode ? 'text-gray-400 hover:text-white disabled:text-gray-600' : 'text-gray-500 hover:text-gray-700 disabled:text-gray-400'}`}
            title="Nachricht senden (Enter)"
          >
            <FiSend />
          </button>
        </div>
      </div>
    </div>
  );
}

export default ChatWindow;
